import type { ResultOf } from '@/graphql'
import type { AccountFragment } from '@/graphql/queries'
import { cn } from '@/lib/utils'

export const stripeSubscriptionStatuses = [
  'active',
  'trialing',
  'past_due',
  'canceled',
  'unpaid',
  'incomplete',
  'incomplete_expired',
  'paused',
  'none',
] as const

export type AccountStripeStatus =
  'notSynced' | 'notInStripe' | (typeof stripeSubscriptionStatuses)[number]

type StripeStatusFields = Pick<
  ResultOf<typeof AccountFragment>,
  'stripeCustomerExists' | 'stripeSubscriptionStatus' | 'stripeStatusSyncedAt'
>

// Checked before stripeCustomerExists because that field reads false until
// the first sync, which would mislabel unsynced accounts as not in Stripe.
export function accountStripeStatus(
  account: StripeStatusFields,
): AccountStripeStatus {
  if (!account.stripeStatusSyncedAt) return 'notSynced'

  if (!account.stripeCustomerExists) return 'notInStripe'

  return account.stripeSubscriptionStatus
}

const statusColorMap: Record<AccountStripeStatus, string> = {
  notSynced:
    'border border-dotted border-gray-400 italic text-gray-500 dark:border-gray-500 dark:text-gray-400',
  notInStripe:
    'border border-dashed border-gray-400 bg-gray-100 text-gray-600 line-through dark:border-gray-500 dark:bg-gray-700 dark:text-gray-300',
  none: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  trialing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  past_due:
    'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  unpaid: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  canceled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  incomplete:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  incomplete_expired:
    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  paused:
    'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
}

export function accountStripeStatusClasses(status: AccountStripeStatus) {
  return cn(
    'inline-flex rounded-full px-2 text-xs font-semibold leading-5',
    statusColorMap[status],
  )
}

export function stripeCustomerUrl(stripeCustomerId: string) {
  return `https://dashboard.stripe.com/customers/${stripeCustomerId}`
}
